// Shared "capture a DOM node to a PNG and share it" helper, extracted from the
// Time Slip share flow so the qualifying sheet behaves identically.
//
// Mobile: uses the Web Share API to open the native sheet with the image + a
// text caption. Desktop (no file share support): downloads the PNG and copies
// the caption to the clipboard as a best-effort fallback.

import { toPng } from "html-to-image";

/**
 * @param {HTMLElement} node   the element to capture
 * @param {object} opts
 * @param {string} opts.fileName   download / share file name (….png)
 * @param {string} opts.title      share sheet title
 * @param {string} opts.text       caption shared alongside the image
 * @param {string} [opts.background="#470271"]  background painted behind the node
 * @param {string} [opts.captureClass="sharing-capture"]  class toggled on the
 *        node for the duration of the capture (reveals capture-only content and
 *        hides interactive chrome via CSS)
 * @param {function} [opts.filter]  html-to-image node filter (exclude elements)
 * @returns {Promise<"shared"|"downloaded">}
 */
export const captureAndShare = async (
  node,
  {
    fileName,
    title,
    text,
    background = "#470271",
    captureClass = "sharing-capture",
    filter,
  } = {},
) => {
  if (!node) return undefined;

  if (captureClass) node.classList.add(captureClass);
  try {
    const dataUrl = await toPng(node, {
      pixelRatio: 2,
      backgroundColor: background,
      cacheBust: true,
      filter,
    });
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], fileName, { type: "image/png" });

    // Mobile: native share sheet with image + caption.
    if (
      typeof navigator !== "undefined" &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({ files: [file], title, text });
      } catch (err) {
        if (err && err.name === "AbortError") return "shared"; // user dismissed
        throw err;
      }
      return "shared";
    }

    // Fallback (most desktops): download the PNG + copy the text summary.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    try {
      await navigator?.clipboard?.writeText?.(text);
    } catch {
      /* clipboard is best-effort */
    }
    return "downloaded";
  } finally {
    if (captureClass) node.classList.remove(captureClass);
  }
};
